/*
 * Back9 quiz — consented tracking runtime.
 *
 * This file, together with quiz-api.js, is the only part of the quiz that
 * touches the network or browser storage. It is deliberately separated from
 * quiz.js: the visual quiz has no idea whether this file was loaded, and runs
 * identically when it was not.
 *
 * That boundary is also where this code is expected to move. The backend's
 * storefront contract assigns the visitor handle, consent gating and outbox to
 * a Theme App Extension adapter rather than to theme assets, and the theme's
 * own security policy (scripts/scan-theme.py) forbids network and storage APIs
 * in theme source. Keeping the two networked files apart means connecting the
 * real app is a file move, not a rewrite.
 *
 * Contract: back9-experiments/docs/QUIZ_BACKEND_CONTRACT.md
 */
(() => {
  'use strict';

  if (window.Back9QuizTracking) return;

  const CONSENT_GRANTED = 'PREFERENCES_AND_ANALYTICS_GRANTED';
  const CONSENT_PREFERENCES = 'PREFERENCES_GRANTED';
  const CONSENT_WITHDRAWN = 'WITHDRAWN';

  const STORAGE = Object.freeze({
    visitor: 'b9_visitor_handle',
    session: 'b9q_session',
    tombstone: 'b9q_withdrawal_pending'
  });

  const MAX_TRANSPORT_ATTEMPTS = 5;
  const MAX_CONFLICT_ATTEMPTS = 3;
  const MAX_START_CONFLICT_ATTEMPTS = 3;
  const MAX_CHALLENGE_ATTEMPTS = 2;
  const EVENT_MAX_AGE_MS = 23 * 60 * 60 * 1000;

  const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
  const backoffDelay = (attempt) => Math.min(400 * (2 ** attempt), 5000) + Math.floor(Math.random() * 200);

  /* ------------------------------------------------------------------ *
   * Consent
   *
   * Both preference and analytics processing must be allowed before any
   * session exists. Anything less runs the quiz locally and silently.
   * ------------------------------------------------------------------ */

  const createConsent = (config) => {
    const listeners = new Set();
    let api = null;
    let state = { preferences: false, analytics: false, resolved: false };

    const read = () => {
      if (config.assumeConsent) return { preferences: true, analytics: true, resolved: true };
      if (!api) return { preferences: false, analytics: false, resolved: false };
      try {
        return {
          preferences: api.preferencesProcessingAllowed() === true,
          analytics: api.analyticsProcessingAllowed() === true,
          resolved: true
        };
      } catch (error) {
        return { preferences: false, analytics: false, resolved: false };
      }
    };

    const refresh = () => {
      const next = read();
      const changed = next.preferences !== state.preferences
        || next.analytics !== state.analytics
        || next.resolved !== state.resolved;
      state = next;
      if (changed) listeners.forEach((listener) => listener(state));
      return state;
    };

    const load = () => new Promise((resolve) => {
      if (config.assumeConsent) {
        refresh();
        resolve(state);
        return;
      }
      if (window.Shopify?.customerPrivacy) {
        api = window.Shopify.customerPrivacy;
        resolve(refresh());
        return;
      }
      if (typeof window.Shopify?.loadFeatures !== 'function') {
        resolve(refresh());
        return;
      }
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        api = error ? null : (window.Shopify.customerPrivacy || null);
        resolve(refresh());
      });
    });

    document.addEventListener('visitorConsentCollected', refresh);

    return {
      load,
      refresh,
      get current() { return state; },
      get granted() { return state.preferences && state.analytics; },
      onChange: (listener) => listeners.add(listener)
    };
  };

  /* ------------------------------------------------------------------ *
   * Turnstile
   *
   * Rendered only when the definition asks for it and only when a tracked
   * session is actually going to be attempted. A challenge that fails to
   * load costs us the session, never the quiz.
   * ------------------------------------------------------------------ */

  const createChallenge = (definitionChallenge, container) => {
    const provider = definitionChallenge?.provider;
    if (provider !== 'TURNSTILE' || !definitionChallenge.siteKey || !container) {
      return { required: false, token: async () => null, reset: async () => null };
    }

    let widgetId = null;
    let pending = null;
    let resolvePending = null;

    const newPending = () => {
      pending = new Promise((resolve) => { resolvePending = resolve; });
      return pending;
    };

    const loadScript = () => new Promise((resolve, reject) => {
      if (window.turnstile) return resolve();
      const existing = document.querySelector('script[data-back9-turnstile]');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', reject);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.back9Turnstile = 'true';
      script.addEventListener('load', () => resolve());
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });

    const render = async () => {
      await loadScript();
      if (!window.turnstile) throw new Error('turnstile unavailable');
      container.hidden = false;
      newPending();
      widgetId = window.turnstile.render(container, {
        sitekey: definitionChallenge.siteKey,
        action: definitionChallenge.action || 'quiz_start',
        appearance: 'interaction-only',
        callback: (token) => resolvePending?.(token),
        'error-callback': () => resolvePending?.(null),
        'expired-callback': () => resolvePending?.(null),
        'timeout-callback': () => resolvePending?.(null)
      });
    };

    const withTimeout = (promise) => Promise.race([promise, sleep(20000).then(() => null)]);

    return {
      required: true,
      token: async () => {
        try {
          if (widgetId === null) await render();
          return await withTimeout(pending);
        } catch (error) {
          return null;
        }
      },
      // A rejected token is single-use. Reset gets a genuinely new one without
      // touching the start request's own idempotency.
      reset: async () => {
        try {
          if (widgetId === null || !window.turnstile) return null;
          const next = newPending();
          window.turnstile.reset(widgetId);
          return await withTimeout(next);
        } catch (error) {
          return null;
        }
      }
    };
  };

  /* ------------------------------------------------------------------ *
   * Tracker
   *
   * Owns the session token, the strictly ordered outbox and the retry
   * policy. Every method is fire-and-forget from the UI's point of view.
   * ------------------------------------------------------------------ */

  const createTracker = ({ api, config, model, consent, challenge }) => {
    const Api = window.Back9QuizApi;

    let status = 'IDLE'; // IDLE | STARTING | ACTIVE | STOPPED
    let sessionToken = null;
    let cursor = null;
    let nextSequence = 1;
    let sentPath = [];
    let outbox = [];
    let flushing = false;
    let startPromise = null;

    const readStore = () => {
      try {
        return JSON.parse(window.sessionStorage.getItem(STORAGE.session) || 'null');
      } catch (error) {
        return null;
      }
    };

    const persist = () => {
      if (!consent.granted || !sessionToken) return;
      try {
        window.sessionStorage.setItem(STORAGE.session, JSON.stringify({
          quizKey: model.key,
          definitionVersion: model.definitionVersion,
          definitionHash: model.definitionHash,
          sessionToken,
          nextSequence,
          sentPath,
          outbox
        }));
      } catch (error) {
        /* Storage is unavailable; the session simply will not survive reload. */
      }
    };

    const erase = () => {
      try {
        window.sessionStorage.removeItem(STORAGE.session);
      } catch (error) {
        /* Nothing to clear. */
      }
    };

    const stop = (reason) => {
      if (status === 'STOPPED') return;
      status = 'STOPPED';
      outbox = [];
      erase();
      if (config.debug) console.info('[back9-quiz] tracking stopped:', reason);
    };

    const visitorHandle = async () => {
      try {
        const stored = window.localStorage.getItem(STORAGE.visitor);
        if (stored) return stored;
      } catch (error) {
        return null;
      }

      const requestId = Api.randomId();
      if (!requestId) return null;

      const response = await api.visitor({ requestId, consentState: CONSENT_PREFERENCES });
      const handle = response.ok ? response.body?.visitorHandle : null;
      if (!handle) return null;

      try {
        window.localStorage.setItem(STORAGE.visitor, handle);
      } catch (error) {
        /* The handle still works for this page view. */
      }
      return handle;
    };

    const adoptCursor = (next) => {
      if (!next) return;
      cursor = next;
      if (typeof next.nextSequence === 'number' && next.nextSequence > nextSequence) {
        nextSequence = next.nextSequence;
      }
    };

    const start = async () => {
      if (status !== 'IDLE') return;
      status = 'STARTING';

      // A pending withdrawal blocks this identity from tracking entirely,
      // regardless of what consent currently says.
      try {
        if (window.localStorage.getItem(STORAGE.tombstone)) return stop('pending withdrawal');
      } catch (error) {
        /* Unreadable storage is treated as no tombstone. */
      }

      const restored = readStore();
      if (restored
        && restored.quizKey === model.key
        && restored.definitionHash === model.definitionHash
        && restored.sessionToken) {
        sessionToken = restored.sessionToken;
        nextSequence = restored.nextSequence || 1;
        sentPath = Array.isArray(restored.sentPath) ? restored.sentPath : [];
        outbox = (Array.isArray(restored.outbox) ? restored.outbox : [])
          .filter((item) => Date.now() - new Date(item.occurredAt).getTime() < EVENT_MAX_AGE_MS);

        const reconciled = await api.state({ sessionToken, consentState: CONSENT_GRANTED });
        if (!reconciled.ok) return stop('could not reconcile a restored session');
        adoptCursor(reconciled.body?.state);
        status = 'ACTIVE';
        return;
      }

      const handle = await visitorHandle();
      if (!handle) return stop('no visitor handle');

      const requestId = Api.randomId();
      if (!requestId) return stop('no secure random source');

      let challengeToken = challenge.required ? await challenge.token() : null;
      if (challenge.required && !challengeToken) return stop('no challenge token');

      let conflicts = 0;
      let challenges = 0;

      for (let attempt = 0; attempt < MAX_TRANSPORT_ATTEMPTS; attempt += 1) {
        // The same requestId and the same facts across every retry: that is
        // what makes a lost response recoverable instead of a double count.
        const payload = {
          quizKey: model.key,
          definitionVersion: model.definitionVersion,
          definitionHash: model.definitionHash,
          visitorHandle: handle,
          requestId,
          consentState: CONSENT_GRANTED
        };
        if (challengeToken) payload.challengeToken = challengeToken;

        if (!consent.granted) return stop('consent withdrawn during start');
        const response = await api.start(payload);

        if (response.outcome === Api.OUTCOME.OK) {
          sessionToken = response.body?.sessionToken || null;
          if (!sessionToken) return stop('start returned no session token');
          adoptCursor(response.body?.state);
          status = 'ACTIVE';
          persist();
          return;
        }

        if (response.outcome === Api.OUTCOME.CHALLENGE) {
          challenges += 1;
          if (challenges > MAX_CHALLENGE_ATTEMPTS) return stop('challenge rejected');
          challengeToken = await challenge.reset();
          if (!challengeToken) return stop('no replacement challenge token');
          continue;
        }

        if (response.outcome === Api.OUTCOME.CONFLICT) {
          conflicts += 1;
          if (conflicts >= MAX_START_CONFLICT_ATTEMPTS) return stop('start conflict');
          await sleep(backoffDelay(conflicts));
          continue;
        }

        if (response.outcome === Api.OUTCOME.RETRYABLE) {
          await sleep(backoffDelay(attempt));
          continue;
        }

        return stop(`start rejected (${response.status})`);
      }

      stop('start exhausted its retries');
    };

    const ensureStarted = () => {
      if (!startPromise) startPromise = start().catch(() => stop('start threw'));
      return startPromise;
    };

    const sendOnce = async (item) => {
      const payload = {
        sessionToken,
        eventId: item.eventId,
        sequence: item.sequence,
        occurredAt: item.occurredAt,
        consentState: CONSENT_GRANTED
      };
      if (item.kind === 'ANSWER') {
        payload.questionKey = item.questionKey;
        payload.answerKey = item.answerKey;
      }

      if (item.kind === 'ANSWER') return api.answer(payload);
      if (item.kind === 'COMPLETE') return api.complete(payload);
      return api.cta(payload);
    };

    // Returns 'DONE' | 'RETRY' | 'STOP'.
    const attemptItem = async (item) => {
      const response = await sendOnce(item);

      if (response.outcome === Api.OUTCOME.OK) {
        adoptCursor(response.body?.state);
        return 'DONE';
      }

      if (response.outcome === Api.OUTCOME.RETRYABLE) return 'RETRY';

      if (response.outcome === Api.OUTCOME.CONFLICT) {
        // Reconcile before deciding. Only a server that still expects exactly
        // this event is worth retrying; anything else is stale and terminal.
        const reconciled = await api.state({ sessionToken, consentState: CONSENT_GRANTED });
        if (!reconciled.ok) return 'RETRY';
        adoptCursor(reconciled.body?.state);

        const stillExpected = reconciled.body?.state?.nextSequence === item.sequence
          && (item.kind !== 'ANSWER' || reconciled.body?.state?.currentQuestionKey === item.questionKey);
        if (!stillExpected) return 'STOP';

        item.conflicts = (item.conflicts || 0) + 1;
        return item.conflicts >= MAX_CONFLICT_ATTEMPTS ? 'STOP' : 'RETRY';
      }

      return 'STOP';
    };

    const flush = async () => {
      if (flushing) return;
      flushing = true;

      try {
        await ensureStarted();

        while (status === 'ACTIVE' && outbox.length > 0) {
          // Consent is rechecked before every queued request, not once at
          // the top of the drain.
          consent.refresh();
          if (!consent.granted) return;

          const item = outbox[0];
          if (Date.now() - new Date(item.occurredAt).getTime() > EVENT_MAX_AGE_MS) {
            stop('event aged out');
            return;
          }

          const result = await attemptItem(item);

          if (result === 'DONE') {
            outbox.shift();
            persist();
            continue;
          }
          if (result === 'STOP') {
            stop(`${item.kind} rejected`);
            return;
          }

          item.attempts = (item.attempts || 0) + 1;
          if (item.attempts >= MAX_TRANSPORT_ATTEMPTS) {
            stop(`${item.kind} exhausted its retries`);
            return;
          }
          persist();
          await sleep(backoffDelay(item.attempts));
        }
      } finally {
        flushing = false;
      }
    };

    const enqueue = (item) => {
      if (status === 'STOPPED' || !consent.granted) return;
      const eventId = Api.randomId();
      if (!eventId) return stop('no secure random source');

      outbox.push(Object.assign({ eventId, sequence: nextSequence, occurredAt: Api.timestamp() }, item));
      nextSequence += 1;
      persist();
      flush();
    };

    return {
      get active() { return status !== 'STOPPED'; },
      get diverged() { return status === 'STOPPED'; },

      // `index` is the position of this answer in the local path. Re-answering
      // an earlier question the same way is a no-op; answering it differently
      // means the browser and the server cursor have permanently diverged, and
      // the honest response is to stop counting rather than to guess.
      answer: (index, questionKey, answerKey) => {
        if (status === 'STOPPED' || !consent.granted) return;
        if (index < sentPath.length) {
          const sent = sentPath[index];
          if (sent.questionKey === questionKey && sent.answerKey === answerKey) return;
          stop('the answer path diverged from the recorded session');
          return;
        }
        if (index > sentPath.length) return stop('answer path gap');

        sentPath.push({ questionKey, answerKey });
        enqueue({ kind: 'ANSWER', questionKey, answerKey });
      },

      complete: (pathLength) => {
        if (status === 'STOPPED' || pathLength !== sentPath.length) return;
        enqueue({ kind: 'COMPLETE' });
      },

      cta: (pathLength) => {
        if (status === 'STOPPED' || pathLength !== sentPath.length) return;
        enqueue({ kind: 'CTA' });
      },

      retake: () => stop('quiz retaken'),

      // Withdrawal is consent-independent once it is owed: clear local state
      // no matter what the network says, and keep a tombstone until the
      // backend acknowledges.
      withdraw: async () => {
        stop('analytics withdrawn');
        let handle = null;
        try {
          handle = window.localStorage.getItem(STORAGE.visitor);
          // Only an identity that could have been counted needs blocking.
          if (handle) window.localStorage.setItem(STORAGE.tombstone, '1');
        } catch (error) {
          /* Without storage there is nothing local left to erase. */
        }
        if (!handle) return;

        const response = await api.withdraw({ visitorHandle: handle, consentState: CONSENT_WITHDRAWN });
        if (response.ok) {
          try {
            window.localStorage.removeItem(STORAGE.tombstone);
          } catch (error) {
            /* The tombstone is harmless if it lingers. */
          }
        }
      }
    };
  };
  /* ------------------------------------------------------------------ *
   * Entry point
   *
   * Returns null when tracking must not run at all, so the caller has a
   * single falsy check rather than a consent branch of its own.
   * ------------------------------------------------------------------ */

  const create = async ({ api, config, model, challengeSlot }) => {
    const consent = createConsent({
      assumeConsent: config.mode === 'mock' && config.mockAssumeConsent === true
    });

    await consent.load();
    if (!consent.granted) return null;

    const challenge = createChallenge(model.challenge, challengeSlot);
    const tracker = createTracker({ api, config, model, consent, challenge });

    // Losing analytics mid-quiz withdraws and erases. Regaining it does not
    // retroactively start a session for a quiz already in progress.
    consent.onChange((state) => {
      if (!state.analytics) tracker.withdraw();
    });

    return tracker;
  };

  window.Back9QuizTracking = Object.freeze({ create });
})();
